import React from 'react';
import { bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import {
  maxLength,
  required,
  composeValidators,
  requiredFieldArrayCheckbox,
  validYear,
  validURL,
  maxWords
} from '../../util/validators';
import { Form, Button, FieldTextInput, FieldCheckboxGroup, InlineTextButton } from '../../components';
import { findOptionsForSelectFilter } from '../../util/search';
import config from '../../config';
import {
  COACHING_FORMAT_FILTER_ID,
  COACHING_EXPERIENCE_BY_CAREER_PHASE_FILTER_ID,
  EMPTY_ROLE_ROW,
  EMPTY_CULTURAL_EXPOSURE_ROW,
} from '../../marketplace-custom-config';

import css from './EditListingDescriptionForm.module.css';

const PREVIOUS_CLIENTS_MAX_LENGTH = 250;
const DESCRIPTION_MAX_WORDS = 1000;
const MAX_ROLE_ROWS = 3;
const MAX_CULTURAL_EXPOSURE_ROWS = 10;
const CULTURAL_EXPOSURE_SUMMARY_MAX_LENGTH = 100;

const EditListingDescriptionFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={formRenderProps => {
      const {
        className,
        disabled,
        ready,
        handleSubmit,
        intl,
        invalid,
        pristine,
        saveActionMsg,
        updated,
        updateInProgress,
        fetchErrors,
        filterConfig,
      } = formRenderProps;

      // description
      const descriptionMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.description',
      });
      const descriptionPlaceholderMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.descriptionPlaceholder',
      });
      const descriptionRequiredMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.descriptionRequired',
      });
      const descriptionMaxWordsMessage = intl.formatMessage(
        { id: 'EditListingDescriptionForm.maxWords' },
        {
          maxWords: DESCRIPTION_MAX_WORDS,
        }
      );
      const maxWordsMessage = maxWords(
        descriptionMaxWordsMessage,
        DESCRIPTION_MAX_WORDS
      );

      // introduction video
      const introductionVideoMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.introductionVideo',
      });
      const introductionVideoPlaceholderMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.introductionVideoPlaceholder',
      });
      const introductionVideoInvalidMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.introductionVideoInvalidUrl',
      });

      // coaching experience
      const coachingExperienceMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.coachingExperience',
      });
      const coachingExperiencePlaceholderMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.coachingExperiencePlaceholder',
      });
      const coachingExperienceRequiredMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.coachingExperienceRequired',
      });
      const coachingExperienceInvalidYearMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.coachingExperienceInvalidYear',
      });

      // previous clients
      const previousClientsMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.previousClients',
      });
      const previousClientsPlaceholderMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.previousClientsPlaceholder',
      });
      const previousClientsMaxLengthMessage = intl.formatMessage(
        { id: 'EditListingDescriptionForm.maxLength' },
        {
          maxLength: PREVIOUS_CLIENTS_MAX_LENGTH,
        }
      );
      const maxLengthMessage = maxLength(
        previousClientsMaxLengthMessage,
        PREVIOUS_CLIENTS_MAX_LENGTH
      );

      // coaching format
      const coachingFormatMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.coachingFormat',
      });
      const coachingFormatHelpMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.coachingFormatHelp',
      });
      const coachingFormatRequiredMessage = intl.formatMessage({
        id: 'EditListingDescriptionForm.coachingFormatRequired',
      });

      // coaching experience by career phase
      const coachingExperienceByCareerPhaseLabel = intl.formatMessage({
        id: 'EditListingCoachingExperienceCareerPhaseForm.coachingExperienceByCareerPhaseLabel',
      });
      const coachingExperienceByCareerPhaseHelp = intl.formatMessage({
        id: 'EditListingDescriptionForm.coachingExperienceByCareerPhaseHelp',
      });

      // example coachee titles
      const previousCoacheeTitlesLabel = intl.formatMessage({
        id: 'EditListingExampleCoacheeTitlesForm.previousCoacheeTitlesLabel',
      });
      const previousCoacheeTitlesPlaceholder = intl.formatMessage({
        id: 'EditListingExampleCoacheeTitlesForm.previousCoacheeTitlesPlaceholder',
      });

      // coach career experience
      const coachCareerExperienceTitle = intl.formatMessage({ id: 'EditListingCoachCareerExperienceForm.title' });
      const rolesPrompt = intl.formatMessage({ id: 'EditListingCoachCareerExperienceForm.rolesPrompt' });
      const rolesHelp = intl.formatMessage({ id: 'EditListingCoachCareerExperienceForm.rolesHelp' });
      const roleJobTitleLabel = intl.formatMessage({ id: 'EditListingCoachCareerExperienceForm.roleJobTitleLabel' });
      const roleCompanyLabel = intl.formatMessage({ id: 'EditListingCoachCareerExperienceForm.roleCompanyLabel' });
      const roleDatesLabel = intl.formatMessage({ id: 'EditListingCoachCareerExperienceForm.roleDatesLabel' });
      const culturalExposurePrompt = intl.formatMessage({ id: 'EditListingCoachCareerExperienceForm.culturalExposurePrompt' });
      const culturalExposureHelp = intl.formatMessage({ id: 'EditListingCoachCareerExperienceForm.culturalExposureHelp' });
      const culturalExposureCountryRegionLabel = intl.formatMessage({ id: 'EditListingCoachCareerExperienceForm.culturalExposureCountryRegionLabel' });
      const culturalExposureDatesLabel = intl.formatMessage({ id: 'EditListingCoachCareerExperienceForm.culturalExposureDatesLabel' });
      const culturalExposureSummaryMaxLengthMessage = intl.formatMessage(
        { id: 'EditListingDescriptionForm.maxLength' },
        {
          maxLength: CULTURAL_EXPOSURE_SUMMARY_MAX_LENGTH,
        }
      );
      const culturalExposureSummaryMaxLength = maxLength(
        culturalExposureSummaryMaxLengthMessage,
        CULTURAL_EXPOSURE_SUMMARY_MAX_LENGTH
      );
      const addCulturalExposureLabel = intl.formatMessage({ id: 'EditListingCoachCareerExperienceForm.addCulturalExposure' });
      const addRoleLabel = intl.formatMessage({ id: 'EditListingCoachCareerExperienceForm.addRole' });
      const removeRowLabel = intl.formatMessage({ id: 'EditListingCoachCareerExperienceForm.removeRow' });

      const { updateListingError, createListingDraftError, showListingsError } = fetchErrors || {};
      const errorMessageUpdateListing = updateListingError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingDescriptionForm.updateFailed" />
        </p>
      ) : null;

      // This error happens only on first tab (of EditListingWizard)
      const errorMessageCreateListingDraft = createListingDraftError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingDescriptionForm.createListingDraftError" />
        </p>
      ) : null;

      const errorMessageShowListing = showListingsError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingDescriptionForm.showListingFailed" />
        </p>
      ) : null;

      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress;
      const submitDisabled = invalid || disabled || submitInProgress;
      const coachingFormatOptions = findOptionsForSelectFilter(
        COACHING_FORMAT_FILTER_ID,
        filterConfig
      );
      const coachingExperienceByCareerPhaseOptions = findOptionsForSelectFilter(
        COACHING_EXPERIENCE_BY_CAREER_PHASE_FILTER_ID,
        filterConfig
      );

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {errorMessageCreateListingDraft}
          {errorMessageUpdateListing}
          {errorMessageShowListing}

          <FieldTextInput
            id="description"
            name="description"
            className={css.description}
            type="textarea"
            label={descriptionMessage}
            placeholder={descriptionPlaceholderMessage}
            autoFocus
            validate={composeValidators(
              required(descriptionRequiredMessage),
              maxWordsMessage
            )}
          />

          <FieldTextInput
            id="introductionVideo"
            name="introductionVideo"
            className={css.description}
            type="text"
            label={introductionVideoMessage}
            placeholder={introductionVideoPlaceholderMessage}
            validate={validURL(introductionVideoInvalidMessage)}
          />

          <FieldTextInput
            id="coachingExperience"
            name="coachingExperience"
            className={css.description}
            type="text"
            label={coachingExperienceMessage}
            placeholder={coachingExperiencePlaceholderMessage}
            validate={composeValidators(
              required(coachingExperienceRequiredMessage),
              validYear(coachingExperienceInvalidYearMessage)
            )}
          />

          <FieldCheckboxGroup
            id="coachingExperienceByCareerPhase"
            name="coachingExperienceByCareerPhase"
            className={css.description}
            label={coachingExperienceByCareerPhaseLabel}
            description={coachingExperienceByCareerPhaseHelp}
            options={coachingExperienceByCareerPhaseOptions}
            twoColumns
          />

          <FieldTextInput
            id="previousClients"
            name="previousClients"
            className={css.description}
            type="text"
            label={previousClientsMessage}
            placeholder={previousClientsPlaceholderMessage}
            validate={maxLengthMessage}
          />

          <div className={css.description}>
            <label htmlFor="previousCoacheeTitles" className={css.fieldLabelText}>
              {previousCoacheeTitlesLabel}
            </label>
            <FieldTextInput
              id="previousCoacheeTitles"
              name="previousCoacheeTitles"
              type="textarea"
              placeholder={previousCoacheeTitlesPlaceholder}
            />
          </div>

          <div className={css.section}>

            <div className={css.subSection}>
              <label className={css.fieldLabelText}>{coachCareerExperienceTitle}</label>
              {rolesHelp ? <p className={css.helpText}>{rolesHelp}</p> : null}
            <div className={css.tableHeader}>
              <span />
              <span className={css.colHeader}>{roleJobTitleLabel}</span>
              <span className={css.colHeader}>{roleCompanyLabel}</span>
              <span className={css.colHeader}>{roleDatesLabel}</span>
              <span />
            </div>
            <FieldArray name="coachCareerExperienceRoles">
              {({ fields }) => (
                <>
                  {fields.map((name, index) => (
                    <div className={css.roleRow} key={name}>
                      <span className={css.rowIndex}>{index + 1}</span>
                      <FieldTextInput
                        id={`${name}.jobTitle`}
                        name={`${name}.jobTitle`}
                        className={css.field}
                        type="text"
                      />
                      <FieldTextInput
                        id={`${name}.company`}
                        name={`${name}.company`}
                        className={css.field}
                        type="text"
                      />
                      <FieldTextInput
                        id={`${name}.dates`}
                        name={`${name}.dates`}
                        className={css.field}
                        type="text"
                      />
                      <InlineTextButton
                        type="button"
                        className={css.removeButton}
                        onClick={() => fields.remove(index)}
                      >
                        {removeRowLabel}
                      </InlineTextButton>
                    </div>
                  ))}
                  {fields.length < MAX_ROLE_ROWS ? (
                    <div className={css.addButtonRight}>
                      <InlineTextButton
                        type="button"
                        className={css.addButton}
                        onClick={() => fields.push({ ...EMPTY_ROLE_ROW })}
                      >
                        {addRoleLabel}
                      </InlineTextButton>
                    </div>
                  ) : null}
                </>
              )}
            </FieldArray>
            </div>

            <div className={css.subSection}>
              <label className={css.fieldLabelText}>{culturalExposurePrompt}</label>
              <p className={classNames(css.helpText, css.compactHelpText)}>{culturalExposureHelp}</p>
            <div className={css.culturalTableHeader}>
              <span />
              <span className={css.colHeader}>{culturalExposureCountryRegionLabel}</span>
              <span className={css.colHeader}>{culturalExposureDatesLabel}</span>
              <span />
            </div>
            <FieldArray name="coachCareerExperienceCulturalExposure">
              {({ fields }) => (
                <>
                  {fields.map((name, index) => (
                    <div className={css.culturalExposureRow} key={name}>
                      <span className={css.rowIndex}>{index + 1}</span>
                      <FieldTextInput
                        id={`${name}.countryRegion`}
                        name={`${name}.countryRegion`}
                        className={css.field}
                        type="text"
                      />
                      <FieldTextInput
                        id={`${name}.dates`}
                        name={`${name}.dates`}
                        className={css.field}
                        type="text"
                        validate={culturalExposureSummaryMaxLength}
                      />
                      <InlineTextButton
                        type="button"
                        className={css.removeButton}
                        onClick={() => fields.remove(index)}
                      >
                        {removeRowLabel}
                      </InlineTextButton>
                    </div>
                  ))}
                  {fields.length < MAX_CULTURAL_EXPOSURE_ROWS ? (
                    <div className={css.addButtonRight}>
                      <InlineTextButton
                        type="button"
                        className={css.addButton}
                        onClick={() => fields.push({ ...EMPTY_CULTURAL_EXPOSURE_ROW })}
                      >
                        {addCulturalExposureLabel}
                      </InlineTextButton>
                    </div>
                  ) : null}
                </>
              )}
            </FieldArray>
            </div>
          </div>

          <FieldCheckboxGroup
            id="coachingFormat"
            name="coachingFormat"
            className={css.coachingFormat}
            label={coachingFormatMessage}
            description={coachingFormatHelpMessage}
            validate={requiredFieldArrayCheckbox(coachingFormatRequiredMessage)}
            options={coachingFormatOptions}
          />

          <Button
            className={css.submitButton}
            type="submit"
            inProgress={submitInProgress}
            disabled={submitDisabled}
            ready={submitReady}
          >
            {saveActionMsg}
          </Button>
        </Form>
      );
    }}
  />
);

EditListingDescriptionFormComponent.defaultProps = {
  className: null,
  fetchErrors: null,
  filterConfig: config.custom.filters,
};

EditListingDescriptionFormComponent.propTypes = {
  className: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    createListingDraftError: propTypes.error,
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  filterConfig: propTypes.filterConfig,
};

export default compose(injectIntl)(EditListingDescriptionFormComponent);
